import { BoxOutlineGeometry, Cartesian3, Color, ColorGeometryInstanceAttribute, GeometryInstance, Matrix4, PerInstanceColorAppearance, Primitive, } from "cesium";
/**
 * OBB (Oriented Bounding Box) helper for displaying wireframe box
 * Displays box with the solid mesh's own modelMatrix so it overlays the mesh
 */
export class AABBHelper {
    _viewer;
    _primitive = null;
    _isVisible = false;
    _lastDimensions = null;
    constructor(viewer) {
        this._viewer = viewer;
    }
    /**
     * Shows the OBB wireframe with the same modelMatrix as the simple structure.
     * @param dimensions - Unscaled dimensions (scale is in modelMatrix)
     * @param modelMatrix - The simple structure's modelMatrix
     * @param color - Line color (default: cyan)
     */
    show(dimensions, modelMatrix, color = Color.CYAN) {
        this.hide();
        // Create geometry with dimensions only (no scale baked in)
        const boxOutline = BoxOutlineGeometry.fromDimensions({
            dimensions: Cartesian3.clone(dimensions),
        });
        const instance = new GeometryInstance({
            geometry: boxOutline,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(color),
            },
        });
        this._primitive = new Primitive({
            geometryInstances: instance,
            modelMatrix: Matrix4.clone(modelMatrix),
            asynchronous: false,
            appearance: new PerInstanceColorAppearance({
                flat: true,
                renderState: {
                    lineWidth: Math.min(2.0, this._viewer.scene.maximumAliasedLineWidth),
                },
            }),
        });
        this._viewer.scene.primitives.add(this._primitive);
        this._isVisible = true;
        this._lastDimensions = {
            x: dimensions.x,
            y: dimensions.y,
            z: dimensions.z,
        };
        this._viewer.scene.requestRender();
    }
    /**
     * Updates the OBB: swaps only modelMatrix when dimensions are unchanged, otherwise rebuilds it.
     * @param dimensions - Unscaled dimensions
     * @param modelMatrix - The simple structure's modelMatrix
     * @param color - Line color
     */
    update(dimensions, modelMatrix, color = Color.CYAN) {
        // Fast path: dimensions unchanged → just update modelMatrix
        if (this._primitive && this._lastDimensions) {
            const dimSame = this._lastDimensions.x === dimensions.x &&
                this._lastDimensions.y === dimensions.y &&
                this._lastDimensions.z === dimensions.z;
            if (dimSame) {
                this._primitive.modelMatrix = Matrix4.clone(modelMatrix);
                return;
            }
        }
        // Slow path: recreate geometry
        this.show(dimensions, modelMatrix, color);
    }
    /**
     * Hides the OBB helper
     */
    hide() {
        if (this._primitive) {
            this._viewer.scene.primitives.remove(this._primitive);
            this._primitive = null;
        }
        this._isVisible = false;
    }
    /**
     * Destroys the OBB helper
     */
    destroy() {
        this.hide();
    }
    /**
     * Returns whether the OBB is currently visible
     */
    get isVisible() {
        return this._isVisible;
    }
}
//# sourceMappingURL=aabb-helper.js.map